/**
 * PDF Utility Functions
 * Common operations used across different PDF generators
 */

import { enqueueSnackbar } from 'notistack'
import { IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'
import React, { Fragment } from 'react'

import { map, zip, sum, concat, flatMap } from '@/utils/lodash'
import { renderBarChartToDataUrl } from '@/utils/canvasBarChart'
import { DEFAULT_ANALYTICS } from '@/housefolios-components/Property/fragments'
import { currencyFormat } from '@/utils/number'
import { formatDateMdY } from '@/utils/date'

const FALLBACK_IMAGE_PATH = '/images/default_image.png'
const MAX_PROFORMA_GALLERY_IMAGES = 15
let cachedFallbackImageDataUrl = null
let pdfGenerationInProgress = false

const isValidPdfImage = (value) =>
  typeof value === 'string' &&
  (value.startsWith('data:image/jpeg') ||
    value.startsWith('data:image/jpg') ||
    value.startsWith('data:image/png'))

const isPdfNativeMimeType = (mimeType = '') => {
  const normalized = mimeType.toLowerCase()
  return (
    normalized === 'image/jpeg' ||
    normalized === 'image/jpg' ||
    normalized === 'image/pjpeg' ||
    normalized === 'image/png'
  )
}

const resolveImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl

  if (
    imageUrl.startsWith('http://') ||
    imageUrl.startsWith('https://') ||
    imageUrl.startsWith('data:')
  ) {
    return imageUrl
  }

  if (imageUrl.startsWith('//')) {
    return `${window.location.protocol}${imageUrl}`
  }

  if (typeof window === 'undefined') return imageUrl

  return new URL(imageUrl, window.location.origin).href
}

async function loadRemoteImageViaCanvas(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.setAttribute('crossOrigin', 'anonymous')

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        canvas.getContext('2d').drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/jpeg', 0.92))
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error(`Failed to load image ${imageUrl}`))
    }

    img.src = imageUrl
  })
}

async function getFallbackImageDataUrl() {
  if (cachedFallbackImageDataUrl) return cachedFallbackImageDataUrl

  const fallbackUrl = resolveImageUrl(FALLBACK_IMAGE_PATH)

  try {
    cachedFallbackImageDataUrl = await loadRemoteImageViaCanvas(fallbackUrl)
  } catch (_canvasError) {
    cachedFallbackImageDataUrl = ''
  }

  return cachedFallbackImageDataUrl
}

export const sanitizePdfDocDefinition = (node) => {
  if (Array.isArray(node)) {
    return node.map((item) => sanitizePdfDocDefinition(item))
  }

  if (!node || typeof node !== 'object') return node

  const sanitized = { ...node }

  if ('image' in sanitized) {
    const { image } = sanitized
    if (
      image == null ||
      image === '' ||
      (typeof image === 'string' && !isValidPdfImage(image))
    ) {
      delete sanitized.image
    }
  }

  for (const key of Object.keys(sanitized)) {
    const value = sanitized[key]
    if (value && typeof value === 'object') {
      sanitized[key] = sanitizePdfDocDefinition(value)
    }
  }

  return sanitized
}

export function createPdfOutputPromise(pdf, method, timeoutMs = 120000) {
  return new Promise((resolve, reject) => {
    let settled = false
    pdfGenerationInProgress = true

    const finish = (handler, value) => {
      if (settled) return
      settled = true
      pdfGenerationInProgress = false
      clearTimeout(timer)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      handler(value)
    }

    const fail = (error) => {
      finish(reject, error instanceof Error ? error : new Error(String(error ?? 'PDF generation failed')))
    }

    const succeed = (value) => {
      finish(resolve, value)
    }

    const timer = setTimeout(() => {
      fail(new Error('PDF generation timed out'))
    }, timeoutMs)

    const onUnhandledRejection = (event) => {
      if (!pdfGenerationInProgress || settled) return
      fail(event.reason)
      event.preventDefault?.()
    }

    window.addEventListener('unhandledrejection', onUnhandledRejection)

    // pdfmake 0.3 returns promises; 0.2 used callbacks — support both.
    const run = async () => {
      try {
        if (method === 'blob') {
          const blob =
            typeof pdf.getBlob === 'function'
              ? await Promise.resolve(pdf.getBlob())
              : null
          if (!blob) {
            fail(new Error('PDF generation returned an empty blob'))
            return
          }
          succeed(blob)
        } else {
          const dataURL =
            typeof pdf.getDataUrl === 'function'
              ? await Promise.resolve(pdf.getDataUrl())
              : null
          if (!dataURL) {
            fail(new Error('PDF generation returned an empty data URL'))
            return
          }
          succeed(dataURL)
        }
      } catch (error) {
        fail(error)
      }
    }

    run()
  })
}

export const limitProformaGalleryImages = (images = []) =>
  images.slice(0, MAX_PROFORMA_GALLERY_IMAGES)

/** @deprecated Import formatDateMdY from @/utils/date in new code */
export const formatDate = formatDateMdY

/**
 * Convert image URL to base64 data URL
 */
export const handleImageUrl = async (imageUrl, closeSnackbar) => {
  if (!imageUrl) return ''

  const resolvedUrl = resolveImageUrl(imageUrl)

  try {
    let dataURL = ''

    // Try the canvas/Image path first when the URL has no extension or could
    // be served as application/octet-stream by S3 — pdfMake only accepts
    // data URLs whose mime is image/jpeg or image/png, so this normalizes.
    try {
      const result = await getBase64ImageFromUrl(resolvedUrl)
      const fetchedDataUrl = result[0]
      const mimeType = result[1]?.type || ''

      if (
        typeof fetchedDataUrl === 'string' &&
        fetchedDataUrl.startsWith('data:') &&
        isPdfNativeMimeType(mimeType) &&
        isValidPdfImage(fetchedDataUrl)
      ) {
        dataURL = fetchedDataUrl
      }
    } catch (_fetchError) {
      // Swallow — we'll fall through to the canvas-based loader.
    }

    if (!isValidPdfImage(dataURL)) {
      // Either the fetch failed or the server returned a non-image/* mime
      // type (common for S3 objects without an extension). Re-load through
      // an <img> + canvas so we always get a valid image/jpeg data URL.
      dataURL = await loadRemoteImageViaCanvas(resolvedUrl)
    }

    if (!isValidPdfImage(dataURL)) {
      throw new Error(`Invalid image response for ${resolvedUrl}`)
    }

    return dataURL
  } catch (err) {
    console.error('handleImageUrl failed', resolvedUrl, err)
    if (closeSnackbar) {
      enqueueSnackbar(
        `Failed to fetch image. If problem persists, contact support`,
        {
          variant: 'error',
          action: (key) => (
            <Fragment>
              <IconButton
                onClick={() => {
                  closeSnackbar(key)
                }}
                size="large"
              >
                <Close style={{ color: 'white' }} />
              </IconButton>
            </Fragment>
          ),
        },
      )
    }

    return (await getFallbackImageDataUrl()) || ''
  }
}

/**
 * Get base64 image from URL
 */
export async function getBase64ImageFromUrl(imageUrl) {
  const resolvedUrl = resolveImageUrl(imageUrl)
  const res = await fetch(resolvedUrl, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-cache',
  })

  if (!res.ok) {
    throw new Error(`Failed to fetch image (${res.status })`)
  }

  const blob = await res.blob()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener(
      'load',
      function () {
        resolve([reader.result, blob])
      },
      false,
    )

    reader.onerror = () => {
      return reject(this)
    }
    reader.readAsDataURL(blob)
  })
}

/**
 * Convert image to JPEG base64 data URL
 */
export async function getBase64JPEGImageFromURL(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.setAttribute('crossOrigin', 'anonymous')

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height

      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)

      const dataURL = canvas.toDataURL('image/jpeg')
      resolve(dataURL)
    }

    img.onerror = (error) => {
      reject(error)
    }

    img.src = url
  })
}

/**
 * Format phone number for display
 */
export const formatPhoneNumber = (str) => {
  const cleaned = ('' + str).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3 })(\d{3 })(\d{4 })$/)
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }
  return str
}

export const graphSection = (args) => {
  const {
    financeAmount,
    mortgagePayment,
    monthlyRate,
    debtYear,
    paydownMonthly,
    holdingPeriod,
  } = args

  let principal = 0
  let amtLeft = 0
  let equity = 0
  let graphData = []
  let loanBalance = 0
  if (holdingPeriod === false) {
    for (let index = 1; index < debtYear + 1; index++) {
      if (index === 1) {
        principal = mortgagePayment - financeAmount * monthlyRate
        amtLeft = financeAmount - principal - paydownMonthly
      } else if (index % 12 === 0) {
        principal = mortgagePayment - amtLeft * monthlyRate
        amtLeft = amtLeft - principal - paydownMonthly

        equity = financeAmount - amtLeft

        if (amtLeft < 0) amtLeft = 0
        graphData.push({
          debt: amtLeft,
          equity: equity,
        })
      } else {
        principal = mortgagePayment - amtLeft * monthlyRate
        amtLeft = amtLeft - principal - paydownMonthly
      }
    }

    return graphData
  } else {
    for (let index = 1; index < debtYear + 1; index++) {
      if (index === 1) {
        principal = mortgagePayment - financeAmount * monthlyRate
        amtLeft = financeAmount - principal - paydownMonthly
      } else if (index === debtYear) {
        principal = mortgagePayment - amtLeft * monthlyRate
        amtLeft = amtLeft - principal - paydownMonthly

        if (amtLeft <= 0) amtLeft = 0

        loanBalance = amtLeft
      } else {
        principal = mortgagePayment - amtLeft * monthlyRate
        amtLeft = amtLeft - principal - paydownMonthly
      }
    }

    return loanBalance
  }
}

export const appreciationSection = (args) => {
  const { afterRepairValue, homePriceAppreciation, term } = args

  let monthlyAppreciation = 0
  let appreciation = []
  let monthlyPriceAppreciation = homePriceAppreciation / 12 / 100
  let yearlyAppreciation = 0

  for (let index = 1; index < term + 1; index++) {
    if (index === 1) {
      monthlyAppreciation = afterRepairValue
    } else if (index % 12 === 0) {
      monthlyAppreciation =
        monthlyAppreciation +
        monthlyAppreciation * monthlyPriceAppreciation
      yearlyAppreciation = monthlyAppreciation - afterRepairValue

      appreciation.push({
        appreciation: yearlyAppreciation,
      })
    } else {
      monthlyAppreciation =
        monthlyAppreciation +
        monthlyAppreciation * monthlyPriceAppreciation
    }
  }

  return appreciation
}

export const revenuesSection = (args) => {
  const { revenueYear, rentAmount, annualRevenueIncrease } = args

  let rentalIncome = []

  for (let index = 1; index < revenueYear + 1; index++) {
    if (index === 1)
      rentalIncome = map(rentAmount, (amount) => amount * 12)
    else
      rentalIncome = map(
        rentalIncome,
        (rentalIncome) => rentalIncome * (1 + annualRevenueIncrease),
      )
  }
  return rentalIncome
}
export const revenuesSectionSTR = (args) => {
  const { revenueYear, avgMonthlyRate, annualRevenueIncrease } = args

  let rentalIncome = []

  for (let index = 1; index < revenueYear + 1; index++) {
    if (index === 1)
      rentalIncome = map(avgMonthlyRate, (amount) => amount * 12)
    else
      rentalIncome = map(
        rentalIncome,
        (rentalIncome) => rentalIncome * (1 + annualRevenueIncrease),
      )
  }
  return rentalIncome
}

export const cashFlowAccumulationCalc = (args) => {
  const {
    accelerator,
    term,
    rentAmount,
    vacancy,
    propertyTaxes,
    homeInsurance,
    monthlyHOA,
    propertyMgmtPercent,
    maintenance,
    utilities,
    annualRevenueIncrease,
    annualOperatingExpenseIncrease,
    //added so that cash flow is with Financing
    mortgagePayment,
  } = args

  let pdAccelerator = accelerator / 100
  let rent = rentAmount
  let rentIncrease = map(rent, (value) => 0)
  let vac = map(
    zip(rentAmount, vacancy),
    ([rentAmount, vacancy]) => rentAmount * vacancy,
  )
  let revenue = 0
  let propTax = propertyTaxes
  let propTaxIncrease = 0
  let insurance = homeInsurance
  let insuranceIncrease = 0
  let hoa = monthlyHOA
  let hoaIncrease = 0
  let propMgmt = sum(rentAmount) * propertyMgmtPercent
  let main = sum(rentAmount) * maintenance
  let mainIncrease = 0
  let uti = utilities
  let utiIncrease = 0
  let expenses = 0
  let cashFlow = 0
  let cfyear = []
  //added so that cash flow is with Financing
  let mortgage = 0

  for (let index = 1; index < term; index++) {
    if (index > 1 && index < 12) {
      rent = map(
        zip(rent, rentAmount),
        ([rent, rentAmount]) => rent + rentAmount,
      )
      propTax += propertyTaxes
      insurance += homeInsurance
      hoa += monthlyHOA
      //added so that cash flow is with Financing
      mortgage += mortgagePayment
    } else if (index % 12 === 0) {
      if (index / 12 === 1) {
        rent = map(
          zip(rent, rentAmount),
          ([rent, rentAmount]) => rent + rentAmount,
        )
        vac = map(
          zip(rent, vacancy),
          ([rent, vacancy]) => rent * vacancy,
        )
        revenue = sum(rent) - sum(vac)

        propTax += propertyTaxes
        insurance += homeInsurance
        hoa += monthlyHOA
        propMgmt = revenue * propertyMgmtPercent
        main = sum(rent) * maintenance
        expenses = propTax + insurance + hoa + propMgmt + main + uti

        //added so that cash flow is with Financing
        mortgage += mortgagePayment

        cashFlow = revenue - expenses - mortgage
        cashFlow = cashFlow - pdAccelerator * cashFlow

        cfyear.push({
          year: index / 12,
          cashFlow: cashFlow,
        })

        rentIncrease = map(
          rentAmount,
          (rentAmount) =>
            rentAmount + rentAmount * annualRevenueIncrease,
        )
        propTaxIncrease =
          propTax * (1 + annualOperatingExpenseIncrease)
        insuranceIncrease =
          insurance * (1 + annualOperatingExpenseIncrease)
        hoaIncrease = hoa * (1 + annualOperatingExpenseIncrease)
        mainIncrease = main * (1 + annualOperatingExpenseIncrease)
        utiIncrease = uti * (1 + annualOperatingExpenseIncrease)
      } else {
        rent = map(
          zip(rent, rentIncrease),
          ([rent, rentIncrease]) => rent + rentIncrease,
        )
        vac = map(
          zip(rent, vacancy),
          ([rent, vacancy]) => rent * vacancy,
        )
        revenue = sum(rent) - sum(vac)

        propTax += propTaxIncrease
        insurance += insuranceIncrease
        hoa += hoaIncrease
        propMgmt = revenue * propertyMgmtPercent
        main += mainIncrease
        uti += utiIncrease
        expenses = propTax + insurance + hoa + propMgmt + main + uti

        //added so that cash flow is with Financing
        mortgage += mortgagePayment

        cashFlow = revenue - expenses - mortgage
        cashFlow = cashFlow - pdAccelerator * cashFlow

        cfyear.push({
          year: index / 12,
          cashFlow: cashFlow,
        })

        rentIncrease = map(
          rentIncrease,
          (rentIncrease) =>
            rentIncrease + rentIncrease * annualRevenueIncrease,
        )
        propTaxIncrease =
          propTaxIncrease * (1 + annualOperatingExpenseIncrease)
        insuranceIncrease =
          insuranceIncrease * (1 + annualOperatingExpenseIncrease)
        hoaIncrease =
          hoaIncrease * (1 + annualOperatingExpenseIncrease)
        mainIncrease =
          mainIncrease * (1 + annualOperatingExpenseIncrease)
        utiIncrease =
          utiIncrease * (1 + annualOperatingExpenseIncrease)
      }
    } else {
      rent = map(
        zip(rent, rentIncrease),
        ([rent, rentIncrease]) => rent + rentIncrease,
      )
      //added so that cash flow is with Financing
      mortgage += mortgagePayment
    }
  }

  return cfyear
}

export const expensesSection = (args) => {
  const {
    expenseType,
    revenueYear,
    propertyTaxes,
    homeInsurance,
    yearlyHOA,
    rentAmount,
    maintenance,
    mgmtFee,
    assetManagementFeeTotal,
    otherExpenses,
    utilitiesTypeDollar,
    yearlyReserve,
    customAnnualExpenseTotal,
    annualOperatingExpenseIncrease,
  } = args

  if (expenseType === 'PT') {
    let ptCalculation = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) ptCalculation = propertyTaxes
      else
        ptCalculation =
          ptCalculation * (1 + annualOperatingExpenseIncrease)
    }

    return ptCalculation
  } else if (expenseType === 'INSURANCE') {
    let hiCalculation = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) hiCalculation = homeInsurance
      else
        hiCalculation =
          hiCalculation * (1 + annualOperatingExpenseIncrease)
    }

    return hiCalculation
  } else if (expenseType === 'HOA') {
    let hoaCalculation = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) hoaCalculation = yearlyHOA
      else
        hoaCalculation =
          hoaCalculation * (1 + annualOperatingExpenseIncrease)
    }

    return hoaCalculation
  } else if (expenseType === 'MAINTENANCE') {
    let mrFee = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) mrFee = rentAmount * maintenance
      else mrFee = mrFee * (1 + annualOperatingExpenseIncrease)
    }

    return mrFee
  } else if (expenseType === 'ASSET') {
    let amf = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) amf = assetManagementFeeTotal
      else amf = assetManagementFeeTotal
    }

    return amf
  } else if (expenseType === 'MGMT') {
    let mgmt = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) mgmt = rentAmount * mgmtFee
      else mgmt = mgmt * (1 + annualOperatingExpenseIncrease)
    }

    return mgmt
  } else if (expenseType === 'OTHER') {
    let other = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) other = otherExpenses
      else other = other * (1 + annualOperatingExpenseIncrease)
    }

    return other
  } else if (expenseType === 'UTILITY') {
    let utility = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) utility = utilitiesTypeDollar
      else utility = utility * (1 + annualOperatingExpenseIncrease)
    }

    return utility
  } else if (expenseType === 'RESERVE') {
    let reserve = 0
    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) reserve = yearlyReserve
      else reserve = reserve * (1 + annualOperatingExpenseIncrease)
    }

    return reserve
  } else if (expenseType === 'CUSTOM') {
    let custom = 0

    for (let index = 1; index < revenueYear + 1; index++) {
      if (index === 1) custom = customAnnualExpenseTotal
      else custom = custom * (1 + annualOperatingExpenseIncrease)
    }

    return custom
  } else {
    return 0
  }
}

/**
 * Generate chart data for property analysis
 */
export const getChartData = (
  property,
  graphTest,
  totalReturnFinancing,
  appreciation,
  cashFlowAccumulation,
  totalInitialInvestmentFinancing,
) => {
  // Calculate values based on property data
  // This is a simplified version - the full implementation would include
  // all the complex calculations from the original file

  let debt1 = 0
  let debt3 = 0
  let debt5 = 0
  let debt10 = 0
  let equity1 = 0
  let equity3 = 0
  let equity5 = 0
  let equity10 = 0
  let cashFlow1 = 0
  let cashFlow3 = 0
  let cashFlow5 = 0
  let cashFlow10 = 0
  let appreciation1 = 0
  let appreciation3 = 0
  let appreciation5 = 0
  let appreciation10 = 0

  for (let key in graphTest) {
    if (key === '0') {
      const tmp = graphTest[key]
      debt1 = tmp.debt
      equity1 = tmp.equity
    } else if (key === '2') {
      const tmp = graphTest[key]
      debt3 = tmp.debt
      equity3 = tmp.equity
    } else if (key === '4') {
      const tmp = graphTest[key]
      debt5 = tmp.debt
      equity5 = tmp.equity
    } else if (key === '9') {
      const tmp = graphTest[key]
      debt10 = tmp.debt
      equity10 = tmp.equity
    }
  }

  for (let key in cashFlowAccumulation) {
    const tmp = cashFlowAccumulation[key]
    if (tmp.year === 1) cashFlow1 = tmp.cashFlow
    if (tmp.year === 3) cashFlow3 = tmp.cashFlow
    if (tmp.year === 5) cashFlow5 = tmp.cashFlow
    if (tmp.year === 10) cashFlow10 = tmp.cashFlow
  }

  for (let key in appreciation) {
    if (key === '0') {
      const tmp = appreciation[key]
      appreciation1 = tmp.appreciation
    } else if (key === '2') {
      const tmp = appreciation[key]
      appreciation3 = tmp.appreciation
    } else if (key === '4') {
      const tmp = appreciation[key]
      appreciation5 = tmp.appreciation
    } else if (key === '9') {
      const tmp = appreciation[key]
      appreciation10 = tmp.appreciation
    }
  }

  const dollarTick = (value, index) =>
    index % 2 === 1 ? '' : currencyFormat.format(value / 1000) + 'k'

  return renderBarChartToDataUrl({
    width: 600,
    height: 400,
    labels: ['1', '3', '5', '10'],
    datasets: [
      {
        label:
          'Debt (' +
          (debt1 / 1000).toFixed(0) +
          'k, ' +
          (debt3 / 1000).toFixed(0) +
          'k, ' +
          (debt5 / 1000).toFixed(0) +
          'k, ' +
          (debt10 / 1000).toFixed(0) +
          'k)',
        data: [debt1, debt3, debt5, debt10],
        backgroundColor: '#B2BDC8',
      },
      {
        label:
          'Equity (' +
          ((equity1 + totalInitialInvestmentFinancing) / 1000).toFixed(0) +
          'k, ' +
          ((equity3 + totalInitialInvestmentFinancing) / 1000).toFixed(0) +
          'k, ' +
          ((equity5 + totalInitialInvestmentFinancing) / 1000).toFixed(0) +
          'k, ' +
          ((equity10 + totalInitialInvestmentFinancing) / 1000).toFixed(0) +
          'k)',
        data: [
          equity1 + totalInitialInvestmentFinancing,
          equity3 + totalInitialInvestmentFinancing,
          equity5 + totalInitialInvestmentFinancing,
          equity10 + totalInitialInvestmentFinancing,
        ],
        backgroundColor: '#46A3DE',
      },
      {
        label:
          'Appreciation (' +
          (appreciation1 / 1000).toFixed(0) +
          'k, ' +
          (appreciation3 / 1000).toFixed(0) +
          'k, ' +
          (appreciation5 / 1000).toFixed(0) +
          'k, ' +
          (appreciation10 / 1000).toFixed(0) +
          'k)',
        data: [
          appreciation1,
          appreciation3,
          appreciation5,
          appreciation10,
        ],
        backgroundColor: '#79CBFF',
      },
      {
        label:
          'Cash Positioning (' +
          (cashFlow1 / 1000).toFixed(0) +
          'k, ' +
          (cashFlow3 / 1000).toFixed(0) +
          'k, ' +
          (cashFlow5 / 1000).toFixed(0) +
          'k, ' +
          (cashFlow10 / 1000).toFixed(0) +
          'k)',
        data: [cashFlow1, cashFlow3, cashFlow5, cashFlow10],
        backgroundColor: '#2F3E4E',
      },
    ],
    stacked: true,
    showLegend: true,
    formatTick: dollarTick,
    stepSize: 25000,
  })
}

/**
 * Generate Fix & Flip chart data
 */
export const getFixFlipChartData = (property) => {
  const { acquisition = {} } = property
  const { analytics = DEFAULT_ANALYTICS } = acquisition

  const resellPrice = acquisition.analysis.resellPriceFixFlip
    ? acquisition.analysis.resellPriceFixFlip
    : analytics.fixFlip.arvTotalFixFlip || 0
  const expenses =
    analytics.fixFlip.totalOutOfPocketFixFlip +
    analytics.fixFlip.salesCommissionTotalFixFlip +
    analytics.fixFlip.dispositionFeeDollarFixFlip +
    analytics.fixFlip.loanBalanceAtHoldingPeriod
  const netProfit = analytics.fixFlip.saleProceeds

  const dollarTick = (value, index) =>
    index % 2 === 1 ? '' : currencyFormat.format(value / 1000) + 'k'

  return renderBarChartToDataUrl({
    width: 600,
    height: 400,
    labels: ['Resell Price', 'Expenses', 'Net Profit'],
    datasets: [
      {
        data: [resellPrice, expenses, netProfit],
        backgroundColor: ['#1CC943', '#F83244', '#04A3E3'],
      },
    ],
    horizontal: true,
    showLegend: false,
    formatTick: dollarTick,
    stepSize: 25000,
  })
}

/**
 * Generate comparable properties chart data
 */
export const getCompChartData = (property, comps) => {
  const dollarTick = (value, index) =>
    index & 1 ? '' : currencyFormat.format(value / 1000) + 'k'

  return renderBarChartToDataUrl({
    width: 500,
    height: 600,
    labels: concat(
      ['Subject Property'],
      flatMap(
        comps,
        (comp) =>
          `${comp.address} ${comp.city}, ${comp.state} ${comp.zip}`,
      ),
    ),
    datasets: [
      {
        label: 'Sold Price',
        data: concat(
          [property.acquisition.analysis.listPrice],
          flatMap(comps, (comp) => comp.soldPrice),
        ),
        backgroundColor: '#2F3E4E',
      },
    ],
    stacked: true,
    showLegend: true,
    formatTick: dollarTick,
    stepSize: 25000,
  })
}
